/**
 * Email type mappings for Email Pulse analytics
 * Maps internal email type identifiers to user-friendly display names
 */

/**
 * Complete mapping of all email types to friendly names
 */
export const EMAIL_TYPE_MAPPINGS: Record<string, string> = {
  // Admin Emails
  ADMIN_PASSWORD_RESET_EMAIL: 'Admin Password Reset Confirmation',
  ADMIN_PASSWORD_RESET_REQUEST_EMAIL: 'Admin Password Reset Request',
  ADMIN_PASSWORD_UPDATE_EMAIL: 'Admin Password Updated',
  ADMIN_VERIFICATION_EMAIL: 'Admin Account Verification',

  // Customer Emails
  USER_PASSWORD_RESET_EMAIL: 'Customer Password Reset',
  USER_SIGNUP_VERIFICATION_EMAIL: 'Customer Account Verification',

  // Gift Card Emails
  BULK_ADD_GC_TO_WALLET_FAILURE_EMAIL: 'Bulk Gift Card Add to Wallet Failed',
  BULK_ADD_GC_TO_WALLET_SUCCESS_EMAIL: 'Bulk Gift Card Added to Wallet',
  COMP_GIFT_CARD_FAILED_EMAIL: 'Complimentary Gift Card',
  EMAIL_GIFTCARD_ADD_VALUE: 'Gift Card Add Value',
  EMAIL_GIFTCARD_ADD_VALUE_RECEIPT: 'Gift Card Add Value Receipt',
  EMAIL_GIFTCARD_PURCHASE_FOR_SENDER: 'Emailed Gift Card Purchase',
  EMAIL_GIFTCARD_PURCHASE_RECEIPT: 'Emailed Gift Card Receipt',
  GIFTCARD_BALANCE_REPORT_EXPORT_FAILED: 'Gift Card Balance Report Failed',
  GIFTCARD_BALANCE_REPORT_EXPORT_SUCCESS: 'Gift Card Balance Report Ready',
  GIFTCARD_EMAIL: 'Gift Card Email',
  GIFTCARD_EMAIL_RECEIPT: 'Gift Card Receipt',
  GIFTCARD_FAILED_EMAIL: 'Gift Card Email Delivery Failed',
  GIFTCARD_IMPORT_FAILED_EMAIL: 'Gift Card Import Failed',
  GIFTCARD_IMPORT_SUCCESS_EMAIL: 'Gift Card Import Successful',
  GIFTCARD_REMOVAL_FAILED_EMAIL: 'Gift Card Removal Failed',
  GIFTCARD_REMOVAL_SUCCESS_EMAIL: 'Gift Card Removed Successfully',
  GIFTCARD_REFUND_RECEIPT_EMAIL: 'Gift Card Refund Receipt',

  // Order Emails
  ORDER_RECEIVED_NOTIFICATION: 'Order Received',
  ORDER_COMPLETE_NOTIFICATION: 'Order Completed',
  ORDER_CUSTOMER_EMAIL: 'Order Confirmation',
  ORDER_CUSTOMER_NOTIFICATION: 'Order Status Update',
  ORDER_REFUND_RECEIPT_EMAIL: 'Order Refund Receipt',

  // Loyalty Emails
  POINTS_EXPIRY_EMAIL: 'Loyalty Points Expiring Soon',

  // Report & Import Emails
  DAILY_SALES_REPORT_GENERATION_FAILED: 'Daily Sales Report Failed',
  DAILY_SALES_REPORT_GENERATION_SUCCESS: 'Daily Sales Report Ready',
  PHONE_ONLY_FAILED_IMPORT_REPORT_EMAIL: 'Phone-Only Import Failed',
  PHONE_ONLY_IMPORT_SUCCESS_EMAIL: 'Phone-Only Import Successful',
  SMS_SIGNUP_REPORT_EMAIL: 'SMS Signup Report',

  // Other Emails
  GUEST_CHECKOUT_VERIFICATION_EMAIL: 'Guest Checkout Verification',
};

/**
 * Email types that belong to the Orders tab
 */
export const ORDERS_EMAIL_TYPES = [
  'ORDER_RECEIVED_NOTIFICATION',
  'ORDER_COMPLETE_NOTIFICATION',
  'ORDER_CUSTOMER_EMAIL',
  'ORDER_CUSTOMER_NOTIFICATION',
  'ORDER_REFUND_RECEIPT_EMAIL',
];

/**
 * Email types that belong to the Gift Cards tab
 */
export const GIFT_CARDS_EMAIL_TYPES = [
  'BULK_ADD_GC_TO_WALLET_FAILURE_EMAIL',
  'BULK_ADD_GC_TO_WALLET_SUCCESS_EMAIL',
  'COMP_GIFT_CARD_FAILED_EMAIL',
  'EMAIL_GIFTCARD_ADD_VALUE',
  'EMAIL_GIFTCARD_ADD_VALUE_RECEIPT',
  'EMAIL_GIFTCARD_PURCHASE_FOR_SENDER',
  'EMAIL_GIFTCARD_PURCHASE_RECEIPT',
  'GIFTCARD_BALANCE_REPORT_EXPORT_FAILED',
  'GIFTCARD_BALANCE_REPORT_EXPORT_SUCCESS',
  'GIFTCARD_EMAIL',
  'GIFTCARD_EMAIL_RECEIPT',
  'GIFTCARD_FAILED_EMAIL',
  'GIFTCARD_IMPORT_FAILED_EMAIL',
  'GIFTCARD_IMPORT_SUCCESS_EMAIL',
  'GIFTCARD_REMOVAL_FAILED_EMAIL',
  'GIFTCARD_REMOVAL_SUCCESS_EMAIL',
  'GIFTCARD_REFUND_RECEIPT_EMAIL',
];

/**
 * Email types that belong to the Security tab (Admin and Customer account-related)
 */
export const SECURITY_EMAIL_TYPES = [
  'ADMIN_PASSWORD_RESET_EMAIL',
  'ADMIN_PASSWORD_RESET_REQUEST_EMAIL',
  'ADMIN_PASSWORD_UPDATE_EMAIL',
  'ADMIN_VERIFICATION_EMAIL',
  'GUEST_CHECKOUT_VERIFICATION_EMAIL',
  'USER_PASSWORD_RESET_EMAIL',
  'USER_SIGNUP_VERIFICATION_EMAIL',
];

/**
 * Get the friendly display name for an email type
 * @param emailType - Internal email type identifier
 * @returns Friendly display name, or the original value if no mapping exists
 */
export const getEmailTypeFriendlyName = (emailType: string): string => {
  return EMAIL_TYPE_MAPPINGS[emailType] || emailType;
};

/**
 * Get email type filter options for a specific category
 * @param category - The email pulse tab category
 * @returns Array of options with friendly labels and internal IDs
 */
export const getEmailTypeOptions = (
  category: 'ORDERS' | 'GIFT_CARDS' | 'SECURITY',
): Array<{ label: string; id: string }> => {
  let emailTypes: string[];

  switch (category) {
    case 'ORDERS':
      emailTypes = ORDERS_EMAIL_TYPES;
      break;
    case 'GIFT_CARDS':
      emailTypes = GIFT_CARDS_EMAIL_TYPES;
      break;
    case 'SECURITY':
      emailTypes = SECURITY_EMAIL_TYPES;
      break;
  }

  // Add "ALL" option first
  const options = [
    {
      label: 'All',
      id: 'ALL',
    },
  ];

  // Add email type options sorted alphabetically by friendly name
  const emailTypeOptions = emailTypes
    .map(type => ({
      label: getEmailTypeFriendlyName(type),
      id: type,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [...options, ...emailTypeOptions];
};
